import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import { track } from '@tldraw/tldraw'
import Styles from './TextEditingDrawer.module.scss'
import { TEXT_PROPS } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/constants'
import { useRootStore } from 'stores/RootStore'
import { TEXT_PRESETS } from '../TextTab/constants'
import { Pressable } from '@react-aria/interactions'
import { Carousel } from 'components/Carousel/Carousel'
import { TextPreset } from '../TextTab/Types'
import { observer } from 'mobx-react-lite'
import { SeeAllButton } from './SeeAllButton'
import { TextPresetPreview } from './TextPresetPreview'
import { Drawer } from 'components/Drawer'
import { TextStylesDrawer } from '../TextStylesDrawer/TextStylesDrawer'
import { getBackdropColor } from './getBackdropColor'
import { usePreventScroll } from '@react-aria/overlays'
import { useResizeObserver } from '@react-aria/utils'
import { useWindowDimensions } from 'hooks/UseWindowDimensions'
import { toJS } from 'mobx'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { TextEditingDrawerProps } from './Types'
import { Helmet } from 'react-helmet'

export const TextEditingDrawer = track(
  observer(({ onConfirm, ...props }: TextEditingDrawerProps) => {
    const { drawersStore, fonts } = useRootStore()

    const [shapeProps, setShapeProps] = useState(toJS(props.shapeProps))
    const [fontSize, setFontSize] = useState(64)
    const [selectedPreset, setSelectedPreset] = useState<TextPreset | undefined>()
    const placeholder = selectedPreset?.props.text || 'Text'

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const windowDimensions = useWindowDimensions()

    const {
      color,
      fontFamily,
      fontWeight,
      underline,
      strikethrough,
      italic,
      uppercase,
      lineHeight,
      letterSpacing,
      text,
    } = shapeProps

    // Prevents repositioning of the page when the keyboard is opened on mobile
    usePreventScroll()

    useEffect(
      function loadFont() {
        fonts.get(fontFamily)?.load()
      },
      [fontFamily, fonts],
    )

    // Resize the font size based on the height of the textarea so that it fits
    // within the viewport
    useResizeObserver({
      ref: textareaRef,
      onResize: () => {
        if (textareaRef.current) {
          const height = textareaRef.current.scrollHeight
          if (height > windowDimensions.height - 200) {
            setFontSize((c) => c - 1)
          }
          if (height < windowDimensions.height - 300 && fontSize < 64) {
            setFontSize((c) => Math.min(c + 1, 64))
          }
        }
      },
    })

    const style: CSSProperties = {
      ...TEXT_PROPS,
      fontWeight,
      textDecoration: [underline && 'underline', strikethrough && 'line-through'].filter(Boolean).join(' '),
      color,
      fontSize,
      fontFamily: `"${fontFamily}"`,
      lineHeight,
      letterSpacing: `${letterSpacing / 1000}em`,
    }
    if (italic) style.fontStyle = 'italic'
    if (uppercase) style.textTransform = 'uppercase'

    const backdropColor = useMemo(() => getBackdropColor(color), [color])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setShapeProps((c) => ({ ...c, text: e.target.value }))
    }

    const handleCancel = () => {
      textareaRef.current?.blur()

      setTimeout(() => {
        drawersStore.closeDrawer()
      }, 500)
    }

    const handleSubmit = () => {
      textareaRef.current?.blur()

      setTimeout(() => {
        drawersStore.closeDrawer()
        setTimeout(() => {
          onConfirm(shapeProps)
        }, 500)
      }, 500)
    }

    const handlePressTextStyle = (preset: TextPreset) => {
      setSelectedPreset(preset)
      setShapeProps((oldProps) => ({ ...oldProps, ...preset.props, text: oldProps.text }))
    }

    const handlePressSeeAll = () => {
      drawersStore.openDrawer(TextStylesDrawer, {
        onSelect: (newProps) => {
          setShapeProps((oldProps) => ({ ...oldProps, ...newProps, text: oldProps.text }))
          drawersStore.closeDrawer()
        },
      })
    }

    return (
      <>
        <Helmet>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, interactive-widget=resizes-content"
          />
        </Helmet>
        <Drawer.Root {...props} activeSnapPoint={1} snapPoints={[1]}>
          <Drawer.Portal>
            <Drawer.Overlay />
            <Drawer.Content fullHeight data-theme={backdropColor}>
              <Drawer.InnerContent color="var(--light-100)" isSnappedTop>
                <Drawer.Top>
                  <Drawer.Action position="left" onPress={handleCancel} variant="ghost">
                    Cancel
                  </Drawer.Action>
                  <div>
                    <VisuallyHidden>
                      <Drawer.Title>Update Text</Drawer.Title>
                    </VisuallyHidden>
                  </div>
                  <Drawer.Action
                    position="right"
                    variant="ghost"
                    onPress={handleSubmit}
                    isDisabled={text.trim() === ''}
                  >
                    Done
                  </Drawer.Action>
                </Drawer.Top>

                <Drawer.Container>
                  <div className={Styles.container}>
                    <div className={Styles.header}>
                      <div className={Styles.headerRow}>
                        <div className={Styles.carouselContainer}>
                          <Carousel>
                            <SeeAllButton onPress={handlePressSeeAll} />
                            {TEXT_PRESETS.map((preset, i) => (
                              <Pressable
                                key={i}
                                onPress={() => handlePressTextStyle(preset)}
                                aria-label={preset.props.text}
                              >
                                <div>
                                  <TextPresetPreview preset={preset} isSelected={preset === selectedPreset} />
                                </div>
                              </Pressable>
                            ))}
                          </Carousel>
                        </div>
                      </div>
                    </div>

                    <div className={Styles.textareaContainer} style={style}>
                      <div className={Styles.spacerText} aria-hidden>
                        {text.startsWith('\n') && <br />}
                        {text || '&nbsp;'}
                        <br />
                        <br />
                      </div>
                      <textarea
                        aria-label="Text"
                        ref={textareaRef}
                        className={Styles.textarea}
                        value={text}
                        onChange={handleChange}
                        onFocus={(e) => {
                          e.target.setSelectionRange(e.target.value.length, e.target.value.length)
                        }}
                        autoFocus
                        placeholder={placeholder}
                      />
                    </div>
                  </div>
                </Drawer.Container>
              </Drawer.InnerContent>
            </Drawer.Content>
            <Drawer.Overlay />
          </Drawer.Portal>
        </Drawer.Root>
      </>
    )
  }),
)

TextEditingDrawer.displayName = 'TextEditingDrawer'
